import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  VENDOR_FIELDS,
  VENDOR_LABELS,
  type VendorKey,
  vendorPatchBody,
  vendorToggleBody,
} from '../../../features/admin/vendors'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { ApiError } from '../../../lib/api-client'
import { Button, Field, Inline, inputCls } from './controls'
import { Badge, Card, mono, Notice, PageHeader, SkeletonCards } from './ui'

/** Error text for admin mutations; a 409 gets an explicit conflict prefix. */
export function adminError(e: unknown): string {
  const msg = apiErrorMessage(e)
  return e instanceof ApiError && e.status === 409 ? `Conflict: ${msg}` : msg
}

export function AdminPage({
  title,
  subtitle,
  right,
  loading,
  error,
  children,
}: {
  title: string
  subtitle: string
  right?: ReactNode
  loading?: boolean
  error?: boolean
  children: ReactNode
}) {
  return (
    <section>
      <PageHeader title={title} subtitle={subtitle} right={right} />
      {loading ? (
        <SkeletonCards rows={2} />
      ) : error ? (
        <Notice
          tone="error"
          title={`We couldn't load ${title.toLowerCase()}`}
          body="You may not have access, or try again in a moment."
        />
      ) : (
        <div className="mt-8 grid gap-5">{children}</div>
      )}
    </section>
  )
}

/** A masked secret with Reveal; the revealed value auto-masks after 30s. Value lives only in component state. */
function SecretValue({ masked, reveal }: { masked: string; reveal: () => Promise<string> }) {
  const [value, setValue] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )
  const hide = () => {
    if (timer.current) clearTimeout(timer.current)
    setValue(null)
  }
  const show = async () => {
    setBusy(true)
    setErr(null)
    try {
      setValue(await reveal())
      timer.current = setTimeout(() => setValue(null), 30_000)
    } catch (e) {
      setErr(adminError(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate text-[13px]" style={mono}>
          {value ?? (masked || 'Not set')}
        </code>
        {masked ? (
          value ? (
            <Button variant="quiet" onClick={hide}>
              Hide
            </Button>
          ) : (
            <Button variant="quiet" onClick={show} disabled={busy}>
              {busy ? 'Revealing...' : 'Reveal'}
            </Button>
          )
        ) : null}
      </div>
      {value ? <p className="text-[12px] text-[var(--color-muted)]">Hides again in 30 seconds.</p> : null}
      {err ? <Inline tone="bad">{err}</Inline> : null}
    </div>
  )
}

/** One vendor's settings card: view, edit (blank secret = keep), enable toggle, reveal. */
export function VendorCard({
  vendor,
  block,
  pending,
  error,
  onSave,
  reveal,
}: {
  vendor: VendorKey
  block: Record<string, unknown> | null
  pending: boolean
  error: unknown
  onSave: (body: { vendor: VendorKey; data: Record<string, unknown> }) => void
  reveal: (vendor: VendorKey, field: string) => Promise<string>
}) {
  const [edits, setEdits] = useState<Record<string, string>>({})
  const f = VENDOR_FIELDS[vendor]
  const enabled = block?.enabled === true
  const str = (k: string) => (typeof block?.[k] === 'string' ? (block[k] as string) : '')
  const patch = vendorPatchBody(vendor, edits)
  const dirty = Object.keys(patch.data).length > 0
  return (
    <Card
      title={VENDOR_LABELS[vendor]}
      aside={
        <div className="flex items-center gap-2">
          <Badge label={enabled ? 'Enabled' : block ? 'Disabled' : 'Not configured'} tone={enabled ? 'ok' : 'off'} />
          <Button variant="quiet" disabled={pending} onClick={() => onSave(vendorToggleBody(vendor, !enabled))}>
            {enabled ? 'Disable' : 'Enable'}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 p-5 sm:grid-cols-2">
        {f.nonSecret.map((k) => (
          <Field key={k} label={k}>
            <input
              className={inputCls}
              style={mono}
              defaultValue={str(k)}
              onChange={(e) => setEdits((s) => ({ ...s, [k]: e.target.value }))}
            />
          </Field>
        ))}
        {f.secret.map((k) => (
          <div key={k}>
            <span className="mb-1 block text-[12px] font-medium text-[var(--color-muted)]">{k}</span>
            <SecretValue masked={str(k)} reveal={() => reveal(vendor, k)} />
            <input
              aria-label={`New ${k}`}
              className={`${inputCls} mt-2`}
              type="password"
              autoComplete="off"
              placeholder="Leave blank to keep current"
              onChange={(e) => setEdits((s) => ({ ...s, [k]: e.target.value }))}
            />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-end gap-3 border-t border-[var(--color-border)] px-5 py-3">
        {error ? <Inline tone="bad">{adminError(error)}</Inline> : null}
        <Button disabled={!dirty || pending} onClick={() => onSave(patch)}>
          {pending ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </Card>
  )
}
